<template>
  <div class="parameters-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h2 class="page-title">
            <i class="fas fa-cog title-icon" />
            Paramètres
          </h2>
          <p class="page-subtitle">
            <span v-if="isAdmin">Configuration des frais de société</span>
            <span v-else-if="isAdminDeveloper">Configuration développeur</span>
          </p>
        </div>
      </div>
    </div>

    <order-price-limit-settings v-if="isAdminDeveloper" />
    <roadmap-desynchronization-settings v-if="isAdminDeveloper" />

    <!-- Fee Configuration Card -->
    <b-card
      v-if="isAdmin"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-money-bill-wave icon" />
          <h4 class="mb-0">
            Configuration des Frais de Société
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez les lignes de frais société appliquées automatiquement aux factures.
          Chaque ligne peut ajouter ou déduire un montant selon ses propres conditions.
        </p>

        <b-form @submit.prevent="saveConfiguration">
          <div
            v-for="(fee, index) in config.fees"
            :key="fee.local_id"
            class="fee-line-card"
          >
            <div class="fee-line-header">
              <div>
                <strong>Ligne {{ index + 1 }}</strong>
                <span class="fee-operation-pill">
                  Déduction du net
                </span>
              </div>
              <b-button
                v-if="config.fees.length > 1"
                variant="outline-danger"
                size="sm"
                @click="removeFeeLine(index)"
              >
                <i class="fas fa-trash mr-1" />
                Supprimer
              </b-button>
            </div>

            <b-form-group
              label="Nom du paramètre"
              class="form-group-enhanced"
            >
              <b-form-input
                v-model.trim="fee.name"
                maxlength="120"
                placeholder="Ex: Frais société"
                class="form-input-enhanced"
                :state="fee.name ? null : false"
              />
            </b-form-group>

            <b-form-group
              label="Type de valeur"
              class="form-group-enhanced"
            >
              <b-form-radio-group
                v-model="fee.fee_type"
                :options="feeTypeOptions"
                button-variant="outline-primary"
                buttons
                class="fee-type-buttons"
                @change="onFeeTypeChange(index)"
              />
            </b-form-group>

            <b-form-group
              :label="fee.fee_type === 'percentage' ? 'Pourcentage (%)' : 'Montant fixe (DT)'"
              class="form-group-enhanced"
            >
              <div class="input-wrapper">
                <b-form-input
                  v-if="fee.fee_type === 'percentage'"
                  v-model.number="fee.percentage_value"
                  type="text"
                  inputmode="decimal"
                  :formatter="formatPrice"
                  min="0"
                  max="100"
                  placeholder="5.000"
                  class="form-input-enhanced"
                  :state="validateFeeLineValue(fee)"
                  @keypress="allowOnlyDecimalNumbers"
                />
                <b-form-input
                  v-else
                  v-model.number="fee.fixed_amount"
                  type="text"
                  inputmode="decimal"
                  :formatter="formatPrice"
                  min="0"
                  placeholder="1.000"
                  class="form-input-enhanced"
                  :state="validateFeeLineValue(fee)"
                  @keypress="allowOnlyDecimalNumbers"
                />
                <span class="input-suffix">{{ fee.fee_type === 'percentage' ? '%' : 'DT' }}</span>
              </div>
            </b-form-group>

            <b-form-group
              label="Appliquer aux"
              class="form-group-enhanced"
            >
              <b-form-select
                v-model="fee.application_scope"
                :options="scopeOptions"
                class="form-input-enhanced"
              />
            </b-form-group>

            <b-form-group
              v-if="fee.application_scope === 'specific'"
              label="Sélection des fournisseurs"
              class="form-group-enhanced"
            >
              <div class="supplier-filters mb-3">
                <b-form-input
                  v-model="supplierSearch"
                  placeholder="Rechercher un fournisseur..."
                  class="form-input-enhanced"
                />
                <b-form-checkbox
                  v-model="showOnlyNoMatricule"
                  class="mt-2"
                  switch
                >
                  <i class="fas fa-filter mr-1" />
                  Afficher uniquement les fournisseurs sans matricule fiscal
                </b-form-checkbox>
              </div>

              <div class="supplier-list">
                <div
                  v-for="supplier in filteredSuppliers"
                  :key="`${fee.local_id}-${supplier.id}`"
                  class="supplier-item"
                  :class="{ selected: isSupplierSelected(fee, supplier.id) }"
                  @click="toggleSupplier(fee, supplier.id)"
                >
                  <b-form-checkbox
                    :checked="isSupplierSelected(fee, supplier.id)"
                    @click.stop="toggleSupplier(fee, supplier.id)"
                  />
                  <div class="supplier-info">
                    <div class="supplier-name-row">
                      <strong>{{ supplier.username }}</strong>
                      <b-badge
                        v-if="!hasMatriculeFiscal(supplier)"
                        variant="warning"
                        class="ml-2"
                      >
                        <i class="fas fa-exclamation-triangle mr-1" />
                        Sans matricule
                      </b-badge>
                    </div>
                    <small class="text-muted">{{ supplier.email }}</small>
                  </div>
                </div>
              </div>

              <div class="selected-count">
                {{ fee.supplier_ids.length }} fournisseur(s) sélectionné(s)
              </div>
            </b-form-group>

            <b-alert
              v-if="fee.application_scope === 'no_matricule'"
              variant="info"
              show
              class="mt-3"
            >
              <i class="fas fa-info-circle mr-2" />
              Cette ligne sera appliquée aux fournisseurs sans matricule fiscal.
            </b-alert>
          </div>

          <b-button
            variant="outline-primary"
            class="mb-2"
            @click="addFeeLine"
          >
            <i class="fas fa-plus-circle mr-1" />
            Ajouter une ligne
          </b-button>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loading || !isFormValid"
              class="save-btn"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <!-- Current Configuration Display -->
        <div
          v-if="!loading && currentConfig && currentConfig.fees && currentConfig.fees.length"
          class="current-config-display"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <strong>Configuration actuelle:</strong>
            <span>
              {{ currentConfig.fees.length }} ligne(s) active(s)
            </span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Run Sheet Print Configuration Card -->
    <b-card
      v-if="isAdmin"
      id="roadmap-print-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-route icon" />
          <h4 class="mb-0">
            Configuration des Run sheets
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez le numéro de téléphone société affiché dans l'encadré des run sheets imprimées.
        </p>

        <b-form @submit.prevent="saveRoadmapPrintSettings">
          <b-form-group
            label="Téléphone affiché"
            label-for="roadmap-print-phone"
            class="form-group-enhanced"
          >
            <b-form-input
              id="roadmap-print-phone"
              v-model.trim="roadmapPrintConfig.roadmap_print_phone"
              type="tel"
              maxlength="32"
              :placeholder="defaultRoadmapPrintPhone || 'Numéro société par défaut'"
              class="form-input-enhanced"
              :state="validateRoadmapPrintPhone"
            />
            <small class="text-muted">
              Laissez vide pour utiliser le numéro société actuel : {{ defaultRoadmapPrintPhone || '-' }}
            </small>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingRoadmapPrintSettings || validateRoadmapPrintPhone === false"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingRoadmapPrintSettings"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingRoadmapPrintSettings"
          class="current-config-display"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <strong>Numéro affiché sur les run sheets:</strong>
            <span>
              {{ roadmapPrintPhonePreview }}
              <small v-if="!hasCustomRoadmapPrintPhone">(numéro société)</small>
            </span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Supplier Bordereau Display Configuration Card -->
    <b-card
      v-if="isAdmin"
      id="supplier-bordereau-display-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-id-card icon" />
          <h4 class="mb-0">
            Affichage fournisseur sur les bordereaux
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez les informations fournisseur visibles sur les bons de livraison. Si un champ est désactivé, son libellé reste affiché et sa valeur est remplacée par des étoiles.
        </p>

        <b-form @submit.prevent="saveSupplierBordereauDisplaySettings">
          <div class="supplier-display-section-title">
            Règle par défaut
          </div>

          <div class="display-fields-grid mb-2">
            <div
              v-for="field in supplierBordereauFields"
              :key="field.key"
              class="display-field-toggle"
              :class="{ active: supplierBordereauConfig.default_fields[field.key] }"
            >
              <div class="display-field-copy">
                <strong>{{ field.label }}</strong>
                <small>{{ field.help }}</small>
              </div>
              <b-form-checkbox
                v-model="supplierBordereauConfig.default_fields[field.key]"
                switch
                class="display-field-switch"
                :aria-label="field.label"
              />
            </div>
          </div>

          <b-form-group
            class="supplier-sensitive-mask-config mt-2 mb-2"
            label="Masquage Matricule fiscale / CIN"
            description="Quand Matricule fiscale ou CIN est désactivé, choisissez si toute la valeur est remplacée par des étoiles ou seulement les 4 premiers caractères."
          >
            <b-form-radio-group
              v-model="supplierBordereauConfig.sensitive_field_mask_mode"
              :options="supplierBordereauSensitiveMaskOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
            />
          </b-form-group>

          <div
            v-if="supplierBordereauConfig.default_fields.phone_number"
            class="supplier-phone-config mt-2 mb-2"
          >
            <b-form-group
              label="Numéro de téléphone sur les bordereaux"
              description="Choisissez le numéro du fournisseur ou un numéro commun pour tous les bordereaux."
            >
              <b-form-select
                v-model="supplierBordereauConfig.phone_number_source"
                :options="supplierBordereauPhoneSourceOptions"
                class="form-input-enhanced mb-1"
              />
              <b-form-input
                v-if="supplierBordereauConfig.phone_number_source === 'standard'"
                v-model.trim="supplierBordereauConfig.standard_phone_number"
                placeholder="Ex. 70 000 000"
                maxlength="32"
                class="form-input-enhanced"
              />
            </b-form-group>
          </div>

          <div class="supplier-display-section-title mt-2">
            Exceptions par fournisseur
          </div>

          <div
            v-if="supplierBordereauConfig.supplier_overrides.length === 0"
            class="supplier-display-empty"
          >
            Aucun fournisseur avec une règle spécifique.
          </div>

          <div class="supplier-display-overrides">
            <div
              v-for="(override, index) in supplierBordereauConfig.supplier_overrides"
              :key="override.local_id"
              class="supplier-display-override"
            >
              <div class="supplier-display-override-header">
                <b-form-select
                  v-model="override.supplier_id"
                  :options="supplierBordereauSupplierOptions"
                  class="form-input-enhanced"
                />
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="supplier-display-remove"
                  @click="removeSupplierBordereauOverride(index)"
                >
                  <i class="fas fa-trash" />
                </b-button>
              </div>
              <div class="display-fields-grid display-fields-grid--compact mt-1">
                <div
                  v-for="field in supplierBordereauFields"
                  :key="`${override.local_id}-${field.key}`"
                  class="display-field-toggle display-field-toggle--compact"
                  :class="{ active: override.fields[field.key] }"
                >
                  <span>{{ field.label }}</span>
                  <b-form-checkbox
                    v-model="override.fields[field.key]"
                    switch
                    class="display-field-switch"
                    :aria-label="field.label"
                  />
                </div>
              </div>
            </div>
          </div>

          <b-button
            variant="outline-primary"
            class="mb-2"
            @click="addSupplierBordereauOverride"
          >
            <i class="fas fa-plus-circle mr-1" />
            Ajouter un fournisseur
          </b-button>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingSupplierBordereauDisplay || !isSupplierBordereauFormValid"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingSupplierBordereauDisplay"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>
      </div>
    </b-card>

    <b-card
      v-if="isAdminDeveloper"
      id="payment-temporary-return-display-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-barcode icon" />
          <h4 class="mb-0">
            Retours temporaires dans les paiements
          </h4>
        </div>
      </template>
      <div class="config-content">
        <p class="config-description">
          Configurez l'affichage des colis en retour temporaire lors de la création d'un paiement.
        </p>
        <b-form @submit.prevent="savePaymentTemporaryReturnDisplaySettings">
          <b-form-radio-group
            v-model="paymentTemporaryReturnDisplayConfig.display_mode"
            :options="paymentTemporaryReturnDisplayOptions"
            stacked
            class="mb-2"
          />
          <small class="text-muted d-block mb-2">
            En mode scan, le motif déjà attribué est conservé et le colis est déplacé vers « Retour » après son scan.
          </small>
          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingPaymentTemporaryReturnDisplay"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingPaymentTemporaryReturnDisplay"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>
      </div>
    </b-card>

    <!-- Delivery Note Barcode Configuration Card -->
    <b-card
      v-if="isAdminDeveloper"
      id="company-bordereau-display-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-building icon" />
          <h4 class="mb-0">
            Affichage société sur les bordereaux
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez les informations société visibles sur les bons de livraison. Si un champ est masqué, sa valeur est remplacée par des étoiles.
        </p>

        <b-form @submit.prevent="saveCompanyBordereauDisplaySettings">
          <div class="display-fields-grid mb-2">
            <div
              v-for="field in companyBordereauBooleanFields"
              :key="field.key"
              class="display-field-toggle"
              :class="{ active: companyBordereauConfig[field.key] }"
            >
              <div class="display-field-copy">
                <strong>{{ field.label }}</strong>
                <small>{{ field.help }}</small>
              </div>
              <b-form-checkbox
                v-model="companyBordereauConfig[field.key]"
                switch
                class="display-field-switch"
                :aria-label="field.label"
              />
            </div>
          </div>

          <b-form-group
            label="Matricule fiscale"
            class="form-group-enhanced"
          >
            <b-form-radio-group
              v-model="companyBordereauConfig.tax_identification_number_mode"
              :options="companyTaxDisplayModeOptions"
              stacked
            />
          </b-form-group>

          <b-form-group
            label="Source du téléphone"
            class="form-group-enhanced"
          >
            <b-form-radio-group
              v-model="companyBordereauConfig.phone_number_source"
              :options="companyPhoneSourceOptions"
              stacked
            />
          </b-form-group>

          <b-form-group
            v-if="companyBordereauConfig.phone_number_source === 'standard'"
            label="Numéro standard"
            label-for="company-standard-phone"
            class="form-group-enhanced"
          >
            <b-form-input
              id="company-standard-phone"
              v-model.trim="companyBordereauConfig.standard_phone_number"
              maxlength="32"
              placeholder="Ex: 99000000"
              class="form-input-enhanced"
              :state="validateCompanyStandardPhone"
            />
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingCompanyBordereauDisplay || !isCompanyBordereauFormValid"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingCompanyBordereauDisplay"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>
      </div>
    </b-card>

    <b-card
      v-if="isAdmin"
      id="delivery-note-barcode-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-barcode icon" />
          <h4 class="mb-0">
            Code-barres des bordereaux
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez les références imprimées sous forme de QR code et code-barres sur les bons de livraison.
        </p>

        <b-form @submit.prevent="saveDeliveryNoteBarcodeSettings">
          <b-form-group
            label="Mode d'affichage"
            label-for="delivery-note-barcode-mode"
            class="form-group-enhanced"
          >
            <b-form-radio-group
              id="delivery-note-barcode-mode"
              v-model="deliveryNoteBarcodeConfig.delivery_note_barcode_mode"
              :options="deliveryNoteBarcodeModeOptions"
              button-variant="outline-primary"
              buttons
              class="fee-type-buttons"
            />
          </b-form-group>

          <b-alert
            variant="info"
            show
            class="mt-3"
          >
            <i class="fas fa-info-circle mr-2" />
            En mode externe seulement, le bon affiche l'ID externe quand il existe. Si la synchronisation n'a pas encore créé d'ID externe, l'ID interne du colis est utilisé à la place.
          </b-alert>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingDeliveryNoteBarcodeSettings"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingDeliveryNoteBarcodeSettings"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingDeliveryNoteBarcodeSettings && currentDeliveryNoteBarcodeConfig"
          class="current-config-display mt-3"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <span>Mode actuel : <strong>{{ deliveryNoteBarcodeModeLabel(currentDeliveryNoteBarcodeConfig.delivery_note_barcode_mode) }}</strong></span>
          </div>
        </div>
      </div>
    </b-card>

    <b-card
      v-if="isAdmin"
      id="delivery-note-print-note-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-sticky-note icon" />
          <h4 class="mb-0">
            Note d'impression des bordereaux
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez le texte affiché sur l'impression des bons de livraison.
        </p>

        <b-form @submit.prevent="saveDeliveryNotePrintNoteSettings">
          <b-form-group
            label="Texte de la note"
            label-for="delivery-note-print-note"
            class="form-group-enhanced"
          >
            <b-form-textarea
              id="delivery-note-print-note"
              v-model="deliveryNotePrintNoteConfig.note"
              class="form-input-enhanced"
              rows="3"
              max-rows="6"
              maxlength="500"
              dir="auto"
              placeholder="هذي الشركة لا تتحمل مسؤولية التكسير"
            />
            <small class="text-muted">
              Laissez ce champ vide pour ne rien afficher sur le bordereau.
            </small>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingDeliveryNotePrintNoteSettings"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingDeliveryNotePrintNoteSettings"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingDeliveryNotePrintNoteSettings && currentDeliveryNotePrintNoteConfig"
          class="current-config-display mt-3"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <span>Note actuelle : <strong>{{ currentDeliveryNotePrintNoteConfig.note || 'Aucune note' }}</strong></span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Inventory Reminder Configuration Card -->
    <b-card
      v-if="isAdmin"
      id="inventory-settings"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-archive icon" />
          <h4 class="mb-0">
            Configuration inventaire
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Configurez l'heure limite quotidienne utilisée pour rappeler à l'admin qu'un inventaire n'a pas été clôturé aujourd'hui.
        </p>

        <b-form @submit.prevent="saveInventorySettings">
          <b-form-group
            label="Inventaire"
            class="form-group-enhanced"
          >
            <b-form-checkbox
              v-model="inventorySettings.is_enabled"
              switch
              size="lg"
            >
              <span :class="inventorySettings.is_enabled ? 'text-success font-weight-bold' : 'text-muted'">
                {{ inventorySettings.is_enabled ? 'Activé' : 'Désactivé' }}
              </span>
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
            label="Rappel admin"
            class="form-group-enhanced"
          >
            <b-form-checkbox
              v-model="inventorySettings.reminder_enabled"
              switch
              size="lg"
              :disabled="!inventorySettings.is_enabled"
            >
              <span :class="inventorySettings.reminder_enabled && inventorySettings.is_enabled ? 'text-success font-weight-bold' : 'text-muted'">
                {{ inventorySettings.reminder_enabled && inventorySettings.is_enabled ? 'Activé' : 'Désactivé' }}
              </span>
            </b-form-checkbox>
          </b-form-group>

          <b-form-group
            label="Heure limite quotidienne"
            label-for="inventory-daily-due-time"
            class="form-group-enhanced"
          >
            <b-form-input
              id="inventory-daily-due-time"
              v-model="inventorySettings.daily_due_time"
              type="time"
              class="form-input-enhanced"
              :disabled="!inventorySettings.is_enabled"
            />
            <small class="text-muted">
              Le rappel s'affiche après cette heure si aucun inventaire n'est clôturé aujourd'hui.
            </small>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingInventorySettings || !inventorySettings.daily_due_time"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingInventorySettings"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingInventorySettings"
          class="current-config-display"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <strong>Configuration actuelle:</strong>
            <span>
              {{ inventorySettingsSummary }}
            </span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Pending Orders Cleanup Configuration Card -->
    <b-card
      v-if="isAdmin"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-trash-restore icon" />
          <h4 class="mb-0">
            Suppression automatique des colis en attente
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Activez la suppression automatique en soft delete des colis au statut En attente créés depuis plus de
          {{ pendingCleanupConfig.retention_days }} jours. Les autres statuts ne sont pas concernés.
        </p>

        <b-form @submit.prevent="savePendingCleanupSettings">
          <b-form-group
            label="Nettoyage automatique"
            class="form-group-enhanced"
          >
            <div class="cleanup-toggle-panel">
              <b-form-checkbox
                v-model="pendingCleanupConfig.auto_delete_pending_orders_enabled"
                switch
                size="lg"
              >
                <span :class="pendingCleanupConfig.auto_delete_pending_orders_enabled ? 'text-success font-weight-bold' : 'text-muted'">
                  {{ pendingCleanupConfig.auto_delete_pending_orders_enabled ? 'Activé' : 'Désactivé' }}
                </span>
              </b-form-checkbox>
              <span class="cleanup-retention-badge">
                <i class="fas fa-calendar-day mr-1" />
                {{ pendingCleanupConfig.retention_days }} jours
              </span>
            </div>
          </b-form-group>

          <b-alert
            variant="info"
            show
            class="mt-3"
          >
            <i class="fas fa-info-circle mr-2" />
            Cette option est propre à cette société. Elle conserve l'historique du colis et applique uniquement une suppression logique.
          </b-alert>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingPendingCleanupSettings"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingPendingCleanupSettings"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingPendingCleanupSettings"
          class="current-config-display"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <strong>Configuration actuelle:</strong>
            <span>
              {{ pendingCleanupStatusLabel }} - colis En attente après {{ pendingCleanupConfig.retention_days }} jours
            </span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Invoice Configuration Card (admin_developer only) -->
    <b-card
      v-if="isAdminDeveloper"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-file-invoice icon" />
          <h4 class="mb-0">
            Configuration des Factures Fournisseur
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Définissez si une facture peut être générée pour un fournisseur qui n'a aucun colis livré (uniquement des retours).
        </p>

        <b-form @submit.prevent="saveInvoiceConfig">
          <b-form-group
            label="Facturation sans colis livré"
            class="form-group-enhanced"
          >
            <div class="phone-unique-toggle">
              <b-form-checkbox
                v-model="invoiceConfig.allow_invoices_without_delivered_orders"
                switch
                size="lg"
              >
                <span :class="invoiceConfig.allow_invoices_without_delivered_orders ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                  {{ invoiceConfig.allow_invoices_without_delivered_orders ? 'Autorisé — Une facture peut être générée même sans colis livré' : 'Bloqué — Un fournisseur doit avoir au moins un colis livré pour être facturé' }}
                </span>
              </b-form-checkbox>
            </div>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingInvoiceConfig"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingInvoiceConfig"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingInvoiceConfig && currentInvoiceConfig"
          class="current-config-display mt-3"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <span>Facturation sans colis livré : <strong>{{ currentInvoiceConfig.allow_invoices_without_delivered_orders ? 'Autorisée' : 'Bloquée' }}</strong></span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Invoice Configuration Card (admin_developer only) -->
    <b-card
      v-if="isAdminDeveloper"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-file-invoice icon" />
          <h4 class="mb-0">
            Configuration des Factures Fournisseur
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Définissez si une facture peut être générée pour un fournisseur qui n'a aucun colis livré (uniquement des retours).
        </p>

        <b-form @submit.prevent="saveInvoiceConfig">
          <b-form-group
            label="Facturation sans colis livré"
            class="form-group-enhanced"
          >
            <div class="phone-unique-toggle">
              <b-form-checkbox
                v-model="invoiceConfig.allow_invoices_without_delivered_orders"
                switch
                size="lg"
              >
                <span :class="invoiceConfig.allow_invoices_without_delivered_orders ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                  {{ invoiceConfig.allow_invoices_without_delivered_orders ? 'Autorisé — Une facture peut être générée même sans colis livré' : 'Bloqué — Un fournisseur doit avoir au moins un colis livré pour être facturé' }}
                </span>
              </b-form-checkbox>
            </div>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingInvoiceConfig"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingInvoiceConfig"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <div
          v-if="!loadingInvoiceConfig && currentInvoiceConfig"
          class="current-config-display mt-3"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <span>Facturation sans colis livré : <strong>{{ currentInvoiceConfig.allow_invoices_without_delivered_orders ? 'Autorisée' : 'Bloquée' }}</strong></span>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Authentication Configuration Card (admin_developer only) -->
    <b-card
      v-if="isAdminDeveloper"
      class="config-card"
    >
      <template #header>
        <div class="card-header-custom">
          <i class="fas fa-shield-alt icon" />
          <h4 class="mb-0">
            Configuration d'Authentification
          </h4>
        </div>
      </template>

      <div class="config-content">
        <p class="config-description">
          Définissez la méthode d'authentification des utilisateurs et les règles d'unicité du numéro de téléphone pour cette entreprise.
        </p>

        <b-form @submit.prevent="saveAuthConfig">
          <!-- Auth Method -->
          <b-form-group
            label="Méthode d'authentification"
            label-for="auth-method"
            class="form-group-enhanced"
          >
            <b-form-radio-group
              id="auth-method"
              v-model="authConfig.auth_method"
              :options="authMethodOptions"
              button-variant="outline-primary"
              buttons
              class="fee-type-buttons"
            />
          </b-form-group>

          <!-- Auth Method Description -->
          <div class="auth-method-info">
            <div
              v-if="authConfig.auth_method === 'email'"
              class="method-badge email"
            >
              <i class="fas fa-envelope" />
              <span>Les utilisateurs se connectent uniquement par <strong>email</strong>. Le téléphone est optionnel.</span>
            </div>
            <div
              v-else-if="authConfig.auth_method === 'phone'"
              class="method-badge phone"
            >
              <i class="fas fa-phone" />
              <span>Les utilisateurs se connectent uniquement par <strong>téléphone</strong>. Le numéro est obligatoire.</span>
            </div>
            <div
              v-else-if="authConfig.auth_method === 'both'"
              class="method-badge both"
            >
              <i class="fas fa-exchange-alt" />
              <span>Les utilisateurs peuvent se connecter par <strong>email ou téléphone</strong>. Le numéro est obligatoire.</span>
            </div>
          </div>

          <!-- Phone Unique Toggle -->
          <b-form-group
            v-if="authConfig.auth_method === 'phone' || authConfig.auth_method === 'both'"
            label="Unicité du numéro de téléphone"
            class="form-group-enhanced mt-2"
          >
            <div class="phone-unique-toggle">
              <b-form-checkbox
                v-model="authConfig.phone_unique"
                switch
                size="lg"
              >
                <span :class="authConfig.phone_unique ? 'text-success font-weight-bold' : 'text-muted'">
                  {{ authConfig.phone_unique ? 'Activé — Chaque numéro de téléphone doit être unique dans cette entreprise' : 'Désactivé — Plusieurs utilisateurs peuvent partager le même numéro' }}
                </span>
              </b-form-checkbox>
            </div>
          </b-form-group>

          <div class="action-buttons">
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="loadingAuth"
              class="save-btn"
            >
              <b-spinner
                v-if="loadingAuth"
                small
                class="mr-2"
              />
              <i
                v-else
                class="fas fa-save mr-2"
              />
              Enregistrer
            </b-button>
          </div>
        </b-form>

        <!-- Current Auth Config Display -->
        <div
          v-if="!loadingAuth && currentAuthConfig"
          class="current-config-display mt-3"
        >
          <i class="fas fa-info-circle" />
          <div class="config-summary">
            <span>Méthode actuelle : <strong>{{ authMethodLabel(currentAuthConfig.auth_method) }}</strong></span>
            <span v-if="currentAuthConfig.auth_method !== 'email'">Téléphone unique : <strong>{{ currentAuthConfig.phone_unique ? 'Oui' : 'Non' }}</strong></span>
          </div>
        </div>
      </div>
    </b-card>

    <DashboardBanners
      v-if="isAdmin"
      mode="config"
    />
  </div>
</template>

<script>
import DashboardBanners from '@/components/DashboardBanners.vue'
import axios from 'axios'
import storeAuth from '@/store/store'
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BForm,
  BSpinner,
  BFormRadioGroup,
  BFormSelect,
  BFormCheckbox,
} from 'bootstrap-vue'
import OrderPriceLimitSettings from './components/OrderPriceLimitSettings.vue'
import RoadmapDesynchronizationSettings from './components/RoadmapDesynchronizationSettings.vue'

export default {
  name: 'Parameters',
  components: {
    DashboardBanners,
    OrderPriceLimitSettings,
    RoadmapDesynchronizationSettings,
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BForm,
    BSpinner,
    BFormRadioGroup,
    BFormSelect,
    BFormCheckbox,
  },
  data() {
    return {
      config: {
        fees: [this.createDefaultFeeLine()],
      },
      currentConfig: null,
      loading: false,
      suppliers: [],
      supplierSearch: '',
      feeTypeOptions: [
        { text: 'Pourcentage', value: 'percentage' },
        { text: 'Montant Fixe', value: 'fixed_amount' },
      ],
      scopeOptions: [
        { text: 'Toutes les factures', value: 'all' },
        { text: 'Fournisseurs sans matricule fiscal', value: 'no_matricule' },
        { text: 'Fournisseurs spécifiques', value: 'specific' },
      ],
      showOnlyNoMatricule: false,
      // Authentication configuration
      authConfig: {
        auth_method: 'email',
        phone_unique: false,
      },
      currentAuthConfig: null,
      loadingAuth: false,
      // Invoice configuration
      invoiceConfig: {
        allow_invoices_without_delivered_orders: true,
      },
      currentInvoiceConfig: null,
      loadingInvoiceConfig: false,
      authMethodOptions: [
        { text: 'Email', value: 'email' },
        { text: 'Téléphone', value: 'phone' },
        { text: 'Les deux', value: 'both' },
      ],
      // Run sheet print configuration
      roadmapPrintConfig: {
        roadmap_print_phone: '',
      },
      loadingRoadmapPrintSettings: false,
      supplierBordereauFields: [
        { key: 'username', label: 'Nom fournisseur', help: 'Nom commercial ou utilisateur' },
        { key: 'phone_number', label: 'Téléphone', help: 'Numéros du fournisseur' },
        { key: 'address', label: 'Adresse', help: 'Adresse du fournisseur' },
        { key: 'matricule_fiscale', label: 'Matricule fiscale', help: 'Identifiant fiscal' },
        { key: 'cin', label: 'CIN', help: 'Carte identité nationale' },
      ],
      supplierBordereauPhoneSourceOptions: [
        { text: 'Utiliser les numéros du fournisseur', value: 'supplier' },
        { text: 'Utiliser un numéro commun', value: 'standard' },
      ],
      supplierBordereauSensitiveMaskOptions: [
        { text: 'Tout en étoiles', value: 'full' },
        { text: '4 premiers caractères en étoiles', value: 'mask_first_4' },
      ],
      supplierBordereauConfig: {
        default_fields: {
          username: true,
          phone_number: true,
          address: true,
          matricule_fiscale: true,
          cin: true,
        },
        phone_number_source: 'supplier',
        standard_phone_number: '',
        sensitive_field_mask_mode: 'full',
        supplier_overrides: [],
      },
      loadingSupplierBordereauDisplay: false,
      paymentTemporaryReturnDisplayConfig: {
        display_mode: 'automatic_return',
      },
      paymentTemporaryReturnDisplayOptions: [
        {
          text: 'Afficher automatiquement dans « Retour temporaire »',
          value: 'automatic_return',
        },
        {
          text: 'Afficher dans « En cours » jusqu’au scan',
          value: 'scan_required',
        },
      ],
      loadingPaymentTemporaryReturnDisplay: false,
      deliveryNoteBarcodeConfig: {
        delivery_note_barcode_mode: 'order_id_and_external_id',
      },
      currentDeliveryNoteBarcodeConfig: null,
      loadingDeliveryNoteBarcodeSettings: false,
      deliveryNotePrintNoteConfig: {
        note: '',
      },
      currentDeliveryNotePrintNoteConfig: null,
      loadingDeliveryNotePrintNoteSettings: false,
      deliveryNoteBarcodeModeOptions: [
        {
          text: 'ID commande + ID externe',
          value: 'order_id_and_external_id',
        },
        {
          text: 'ID externe seulement',
          value: 'external_id_only',
        },
      ],
      inventorySettings: {
        is_enabled: true,
        reminder_enabled: true,
        daily_due_time: '18:00',
      },
      loadingInventorySettings: false,
      pendingCleanupConfig: {
        auto_delete_pending_orders_enabled: false,
        retention_days: 10,
      },
      loadingPendingCleanupSettings: false,
    }
  },
  computed: {
    isAdmin() {
      const role = storeAuth.getters.userRole || storeAuth.state.user?.role || storeAuth.state.role || localStorage.getItem('role') || ''
      return ['admin', 'superadmin'].includes(role)
    },
    isAdminDeveloper() {
      const role = storeAuth.getters.userRole || storeAuth.state.user?.role || storeAuth.state.role || localStorage.getItem('role') || ''
      return role === 'admin_developer'
    },
    isFormValid() {
      return this.config.fees.length > 0 && this.config.fees.every(fee => this.validateFeeLine(fee))
    },
    defaultRoadmapPrintPhone() {
      return storeAuth.state.company?.phone1 || ''
    },
    hasCustomRoadmapPrintPhone() {
      return Boolean(this.roadmapPrintConfig.roadmap_print_phone && this.roadmapPrintConfig.roadmap_print_phone.trim())
    },
    roadmapPrintPhonePreview() {
      return this.hasCustomRoadmapPrintPhone
        ? this.roadmapPrintConfig.roadmap_print_phone.trim()
        : this.defaultRoadmapPrintPhone || '-'
    },
    validateRoadmapPrintPhone() {
      const phone = this.roadmapPrintConfig.roadmap_print_phone
      if (!phone) return null
      return phone.length <= 32 && /^[0-9+\s()./-]+$/.test(phone)
    },
    validateCompanyStandardPhone() {
      const phone = this.companyBordereauConfig.standard_phone_number
      if (!phone) return null
      return phone.length <= 32 && /^[0-9+\s()./-]+$/.test(phone)
    },
    isCompanyBordereauFormValid() {
      if (this.companyBordereauConfig.phone_number_source !== 'standard') return true
      return Boolean(this.companyBordereauConfig.standard_phone_number && this.validateCompanyStandardPhone)
    },
    supplierBordereauSupplierOptions() {
      return [
        { value: null, text: 'Sélectionner un fournisseur' },
        ...this.suppliers.map(supplier => ({
          value: supplier.id,
          text: supplier.page_name || supplier.username,
        })),
      ]
    },
    isSupplierBordereauFormValid() {
      const ids = this.supplierBordereauConfig.supplier_overrides.map(item => item.supplier_id)
      return ids.every(Boolean) && ids.length === new Set(ids).size
    },
    pendingCleanupStatusLabel() {
      return this.pendingCleanupConfig.auto_delete_pending_orders_enabled ? 'Activée' : 'Désactivée'
    },
    inventorySettingsSummary() {
      if (!this.inventorySettings.is_enabled) return 'Inventaire désactivé'
      const reminderStatus = this.inventorySettings.reminder_enabled ? 'rappel activé' : 'rappel désactivé'
      return `${reminderStatus} - heure limite ${this.inventorySettings.daily_due_time || '-'}`
    },
    filteredSuppliers() {
      let filtered = this.suppliers

      // Search filter
      if (this.supplierSearch) {
        const search = this.supplierSearch.toLowerCase()
        filtered = filtered.filter(s => s.username.toLowerCase().includes(search)
          || s.email.toLowerCase().includes(search))
      }

      // Matricule filter
      if (this.showOnlyNoMatricule) {
        filtered = filtered.filter(s => !this.hasMatriculeFiscal(s))
      }

      return filtered
    },
  },
  watch: {
    '$route.name': 'scrollToRouteSection',
    '$route.hash': 'scrollToRouteSection',
  },
  created() {
    if (this.isAdmin) {
      this.fetchConfiguration()
      this.fetchSuppliers()
      this.fetchRoadmapPrintSettings()
      this.fetchSupplierBordereauDisplaySettings()
      this.fetchDeliveryNoteBarcodeSettings()
      this.fetchDeliveryNotePrintNoteSettings()
      this.fetchInventorySettings()
      this.fetchPendingCleanupSettings()
    }
    if (this.isAdminDeveloper) {
      this.fetchAuthConfig()
      this.fetchPaymentTemporaryReturnDisplaySettings()
      this.fetchCompanyBordereauDisplaySettings()
      this.fetchInvoiceConfig()
    }
  },
  mounted() {
    this.scrollToRouteSection()
  },
  methods: {
    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    scrollToRouteSection() {
      let sectionId = ''
      if (this.$route.name === 'roadmap-print-parameters') {
        sectionId = 'roadmap-print-settings'
      } else if (this.$route.name === 'supplier-bordereau-display-parameters') {
        sectionId = 'supplier-bordereau-display-settings'
      } else if (this.$route.name === 'delivery-note-barcode-parameters') {
        sectionId = 'delivery-note-barcode-settings'
      } else if (this.$route.hash) {
        sectionId = this.$route.hash.replace('#', '')
      }

      if (!sectionId) return

      this.$nextTick(() => {
        const section = document.getElementById(sectionId)
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    async fetchConfiguration() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/core/company-fee/config/')
        const fees = data.fees || (data.is_active !== false ? [data] : [])
        if (fees.length) {
          this.config = {
            fees: fees.map(fee => this.normalizeFeeLine(fee)),
          }
          this.currentConfig = data
        } else {
          this.config = { fees: [this.createDefaultFeeLine()] }
          this.currentConfig = { fees: [] }
        }
      } catch (error) {
        // Silent fail - no active configuration is a valid state
      } finally {
        this.loading = false
      }
    },
    async fetchSuppliers() {
      try {
        const { data } = await axios.get('/api/auth/users/suppliers/')
        this.suppliers = data.results || data
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement des fournisseurs', {
          title: 'Erreur',
          variant: 'danger',
        })
      }
    },
    async saveConfiguration() {
      if (!this.isFormValid) {
        this.$bvToast.toast('Veuillez corriger les erreurs du formulaire', {
          title: 'Erreur',
          variant: 'warning',
        })
        return
      }

      this.loading = true
      try {
        const payload = {
          fees: this.config.fees.map(fee => this.toFeePayload(fee)),
        }

        const { data } = await axios.put('/api/core/company-fee/config/', payload)
        this.currentConfig = data
        this.config = {
          fees: (data.fees || []).map(fee => this.normalizeFeeLine(fee)),
        }

        this.$bvToast.toast('Configuration enregistrée avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loading = false
      }
    },
    createDefaultFeeLine() {
      return {
        local_id: `${Date.now()}-${Math.random()}`,
        id: null,
        name: 'Frais société',
        is_active: true,
        fee_type: 'fixed_amount',
        percentage_value: 0,
        fixed_amount: 0,
        application_scope: 'all',
        supplier_ids: [],
      }
    },
    normalizeFeeLine(fee) {
      return {
        local_id: `${fee.id || Date.now()}-${Math.random()}`,
        id: fee.id || null,
        name: fee.name || 'Frais société',
        is_active: fee.is_active !== false,
        fee_type: fee.fee_type || 'fixed_amount',
        percentage_value: Number(fee.percentage_value || 0),
        fixed_amount: Number(fee.fixed_amount || 0),
        application_scope: fee.application_scope || 'all',
        supplier_ids: fee.supplier_ids || fee.suppliers || [],
      }
    },
    toFeePayload(fee) {
      const payload = {
        name: fee.name,
        is_active: fee.is_active,
        operation: 'subtraction',
        fee_type: fee.fee_type,
        application_scope: fee.application_scope,
        supplier_ids: fee.application_scope === 'specific' ? fee.supplier_ids : [],
      }
      if (fee.fee_type === 'percentage') {
        payload.percentage_value = fee.percentage_value
        payload.fixed_amount = null
      } else {
        payload.fixed_amount = fee.fixed_amount
        payload.percentage_value = null
      }
      return payload
    },
    addFeeLine() {
      this.config.fees.push(this.createDefaultFeeLine())
    },
    removeFeeLine(index) {
      this.config.fees.splice(index, 1)
    },
    validateFeeLineValue(fee) {
      if (fee.fee_type === 'percentage') {
        return fee.percentage_value >= 0 && fee.percentage_value <= 100
      }
      return fee.fixed_amount >= 0
    },
    validateFeeLine(fee) {
      if (!fee.name || !fee.name.trim()) return false
      if (!this.validateFeeLineValue(fee)) return false
      if (fee.application_scope === 'specific' && fee.supplier_ids.length === 0) return false
      return true
    },
    onFeeTypeChange(index) {
      const fee = this.config.fees[index]
      if (!fee) return
      // Reset values when switching types
      if (fee.fee_type === 'percentage') {
        fee.fixed_amount = 0
      } else {
        fee.percentage_value = 0
      }
    },
    toggleSupplier(fee, supplierId) {
      const index = fee.supplier_ids.indexOf(supplierId)
      if (index > -1) {
        fee.supplier_ids.splice(index, 1)
      } else {
        fee.supplier_ids.push(supplierId)
      }
    },
    isSupplierSelected(fee, supplierId) {
      return fee.supplier_ids.includes(supplierId)
    },
    hasMatriculeFiscal(supplier) {
      if (!supplier.tax_identification_number) return false
      const val = String(supplier.tax_identification_number).trim()
      return val !== '' && val !== '0'
    },
    formatCurrency(value) {
      return `${parseFloat(value).toFixed(3)} DT`
    },
    async fetchRoadmapPrintSettings() {
      this.loadingRoadmapPrintSettings = true
      try {
        const { data } = await axios.get('/api/core/parameters/roadmap-print/config/')
        this.roadmapPrintConfig = {
          roadmap_print_phone: data.roadmap_print_phone || '',
        }
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement des paramètres run sheet', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingRoadmapPrintSettings = false
      }
    },
    async saveRoadmapPrintSettings() {
      if (this.validateRoadmapPrintPhone === false) {
        this.$bvToast.toast('Veuillez saisir un numéro de téléphone valide', {
          title: 'Erreur',
          variant: 'warning',
        })
        return
      }

      this.loadingRoadmapPrintSettings = true
      try {
        const payload = {
          roadmap_print_phone: this.roadmapPrintConfig.roadmap_print_phone || '',
        }
        const { data } = await axios.put('/api/core/parameters/roadmap-print/config/', payload)
        this.roadmapPrintConfig = {
          roadmap_print_phone: data.roadmap_print_phone || '',
        }

        this.$bvToast.toast('Paramètres run sheet enregistrés avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement des paramètres run sheet',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loadingRoadmapPrintSettings = false
      }
    },

    createSupplierBordereauFields(fields = {}) {
      return this.supplierBordereauFields.reduce((result, field) => ({
        ...result,
        [field.key]: fields[field.key] !== false,
      }), {})
    },
    normalizeSupplierBordereauConfig(config = {}) {
      return {
        default_fields: this.createSupplierBordereauFields(config.default_fields),
        phone_number_source: ['supplier', 'standard'].includes(config.phone_number_source)
          ? config.phone_number_source
          : 'supplier',
        standard_phone_number: config.standard_phone_number || '',
        sensitive_field_mask_mode: ['full', 'mask_first_4'].includes(config.sensitive_field_mask_mode)
          ? config.sensitive_field_mask_mode
          : 'full',
        supplier_overrides: (config.supplier_overrides || []).map(override => ({
          local_id: `${override.supplier_id || Date.now()}-${Math.random()}`,
          supplier_id: override.supplier_id || null,
          fields: this.createSupplierBordereauFields(override.fields),
        })),
      }
    },
    normalizeCompanyBordereauConfig(config = {}) {
      const taxMode = ['show', 'hide', 'mask_first_4'].includes(config.tax_identification_number_mode)
        ? config.tax_identification_number_mode
        : 'mask_first_4'
      const phoneSource = ['company', 'standard'].includes(config.phone_number_source)
        ? config.phone_number_source
        : 'company'

      return {
        name_visible: config.name_visible !== false,
        address_visible: config.address_visible !== false,
        phone_number_visible: config.phone_number_visible !== false,
        tax_identification_number_mode: taxMode,
        phone_number_source: phoneSource,
        standard_phone_number: config.standard_phone_number || '',
      }
    },
    async fetchSupplierBordereauDisplaySettings() {
      this.loadingSupplierBordereauDisplay = true
      try {
        const { data } = await axios.get('/api/core/parameters/supplier-bordereau-display/config/')
        this.supplierBordereauConfig = this.normalizeSupplierBordereauConfig(data)
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement des paramètres bordereau fournisseur', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingSupplierBordereauDisplay = false
      }
    },
    async fetchCompanyBordereauDisplaySettings() {
      this.loadingCompanyBordereauDisplay = true
      try {
        const { data } = await axios.get('/api/core/parameters/company-bordereau-display/config/')
        this.companyBordereauConfig = this.normalizeCompanyBordereauConfig(data)
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement des paramètres bordereau société', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingCompanyBordereauDisplay = false
      }
    },
    async saveCompanyBordereauDisplaySettings() {
      if (!this.isCompanyBordereauFormValid) {
        this.$bvToast.toast('Veuillez saisir un numéro standard valide', {
          title: 'Erreur',
          variant: 'warning',
        })
        return
      }

      this.loadingCompanyBordereauDisplay = true
      try {
        const payload = this.normalizeCompanyBordereauConfig(this.companyBordereauConfig)
        const { data } = await axios.put('/api/core/parameters/company-bordereau-display/config/', payload)
        this.companyBordereauConfig = this.normalizeCompanyBordereauConfig(data)
        await storeAuth.dispatch('fetchCompanyInfo')
        this.$bvToast.toast('Paramètres bordereau société enregistrés avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement des paramètres bordereau société',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loadingCompanyBordereauDisplay = false
      }
    },
    addSupplierBordereauOverride() {
      this.supplierBordereauConfig.supplier_overrides.push({
        local_id: `${Date.now()}-${Math.random()}`,
        supplier_id: null,
        fields: this.createSupplierBordereauFields(this.supplierBordereauConfig.default_fields),
      })
    },
    removeSupplierBordereauOverride(index) {
      this.supplierBordereauConfig.supplier_overrides.splice(index, 1)
    },
    async saveSupplierBordereauDisplaySettings() {
      if (!this.isSupplierBordereauFormValid) {
        this.$bvToast.toast('Veuillez sélectionner des fournisseurs différents pour chaque ligne', {
          title: 'Erreur',
          variant: 'warning',
        })
        return
      }

      this.loadingSupplierBordereauDisplay = true
      try {
        const payload = {
          default_fields: this.createSupplierBordereauFields(this.supplierBordereauConfig.default_fields),
          phone_number_source: this.supplierBordereauConfig.phone_number_source,
          standard_phone_number: this.supplierBordereauConfig.standard_phone_number,
          sensitive_field_mask_mode: this.supplierBordereauConfig.sensitive_field_mask_mode,
          supplier_overrides: this.supplierBordereauConfig.supplier_overrides.map(override => ({
            supplier_id: override.supplier_id,
            fields: this.createSupplierBordereauFields(override.fields),
          })),
        }
        const { data } = await axios.put('/api/core/parameters/supplier-bordereau-display/config/', payload)
        this.supplierBordereauConfig = this.normalizeSupplierBordereauConfig(data)
        this.$bvToast.toast('Paramètres bordereau fournisseur enregistrés avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement des paramètres bordereau fournisseur',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loadingSupplierBordereauDisplay = false
      }
    },
    async fetchPaymentTemporaryReturnDisplaySettings() {
      this.loadingPaymentTemporaryReturnDisplay = true
      try {
        const { data } = await axios.get('/api/core/parameters/payment-temporary-return-display/config/')
        this.paymentTemporaryReturnDisplayConfig = {
          display_mode: ['automatic_return', 'scan_required'].includes(data.display_mode)
            ? data.display_mode
            : 'automatic_return',
        }
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement du paramétrage des retours temporaires', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingPaymentTemporaryReturnDisplay = false
      }
    },
    async savePaymentTemporaryReturnDisplaySettings() {
      this.loadingPaymentTemporaryReturnDisplay = true
      try {
        const { data } = await axios.put('/api/core/parameters/payment-temporary-return-display/config/', {
          display_mode: this.paymentTemporaryReturnDisplayConfig.display_mode,
        })
        this.paymentTemporaryReturnDisplayConfig = {
          display_mode: data.display_mode,
        }
        this.$bvToast.toast('Paramétrage des retours temporaires enregistré avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.display_mode?.[0] || 'Erreur lors de l\'enregistrement du paramétrage',
          { title: 'Erreur', variant: 'danger' },
        )
      } finally {
        this.loadingPaymentTemporaryReturnDisplay = false
      }
    },

    normalizeDeliveryNoteBarcodeMode(mode) {
      return this.deliveryNoteBarcodeModeOptions.some(option => option.value === mode)
        ? mode
        : 'order_id_and_external_id'
    },
    fetchDeliveryNoteBarcodeSettings() {
      const { company } = storeAuth.state
      const mode = this.normalizeDeliveryNoteBarcodeMode(company && company.delivery_note_barcode_mode)
      this.deliveryNoteBarcodeConfig = {
        delivery_note_barcode_mode: mode,
      }
      this.currentDeliveryNoteBarcodeConfig = {
        delivery_note_barcode_mode: mode,
      }
    },
    deliveryNoteBarcodeModeLabel(mode) {
      const option = this.deliveryNoteBarcodeModeOptions.find(item => item.value === mode)
      return option ? option.text : '-'
    },
    async saveDeliveryNoteBarcodeSettings() {
      this.loadingDeliveryNoteBarcodeSettings = true
      try {
        const { company } = storeAuth.state
        if (!company || !company.id) {
          this.$bvToast.toast('Entreprise introuvable', {
            title: 'Erreur',
            variant: 'danger',
          })
          return
        }

        const payload = {
          delivery_note_barcode_mode: this.normalizeDeliveryNoteBarcodeMode(
            this.deliveryNoteBarcodeConfig.delivery_note_barcode_mode,
          ),
        }

        await axios.patch(`api/companies/update/${company.id}/`, payload)
        this.currentDeliveryNoteBarcodeConfig = { ...payload }
        await storeAuth.dispatch('fetchCompanyInfo')

        this.$bvToast.toast('Configuration des code-barres enregistrée avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement des paramètres code-barres',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loadingDeliveryNoteBarcodeSettings = false
      }
    },
    normalizeDeliveryNotePrintNoteConfig(config = {}) {
      return {
        note: String(config.note || '').trim(),
      }
    },
    async fetchDeliveryNotePrintNoteSettings() {
      this.loadingDeliveryNotePrintNoteSettings = true
      try {
        const { data } = await axios.get('/api/core/parameters/delivery-note-print-note/config/')
        const config = this.normalizeDeliveryNotePrintNoteConfig(data)
        this.deliveryNotePrintNoteConfig = { ...config }
        this.currentDeliveryNotePrintNoteConfig = { ...config }
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement de la note des bordereaux', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingDeliveryNotePrintNoteSettings = false
      }
    },
    async saveDeliveryNotePrintNoteSettings() {
      this.loadingDeliveryNotePrintNoteSettings = true
      try {
        const payload = this.normalizeDeliveryNotePrintNoteConfig(this.deliveryNotePrintNoteConfig)
        const { data } = await axios.put('/api/core/parameters/delivery-note-print-note/config/', payload)
        const config = this.normalizeDeliveryNotePrintNoteConfig(data)
        this.deliveryNotePrintNoteConfig = { ...config }
        this.currentDeliveryNotePrintNoteConfig = { ...config }

        this.$bvToast.toast('Note des bordereaux enregistrée avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement de la note des bordereaux',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loadingDeliveryNotePrintNoteSettings = false
      }
    },
    normalizeInventoryTime(value) {
      if (!value) return '18:00'
      return String(value).slice(0, 5)
    },
    async fetchInventorySettings() {
      this.loadingInventorySettings = true
      try {
        const { data } = await axios.get('/api/inventory/settings/')
        this.inventorySettings = {
          is_enabled: Boolean(data.is_enabled),
          reminder_enabled: Boolean(data.reminder_enabled),
          daily_due_time: this.normalizeInventoryTime(data.daily_due_time),
        }
      } catch (error) {
        // Keep defaults when the backend cannot load settings.
      } finally {
        this.loadingInventorySettings = false
      }
    },
    async saveInventorySettings() {
      this.loadingInventorySettings = true
      try {
        const payload = {
          is_enabled: Boolean(this.inventorySettings.is_enabled),
          reminder_enabled: Boolean(this.inventorySettings.reminder_enabled),
          daily_due_time: this.normalizeInventoryTime(this.inventorySettings.daily_due_time),
        }
        const { data } = await axios.patch('/api/inventory/settings/', payload)
        this.inventorySettings = {
          is_enabled: Boolean(data.is_enabled),
          reminder_enabled: Boolean(data.reminder_enabled),
          daily_due_time: this.normalizeInventoryTime(data.daily_due_time),
        }
        this.$bvToast.toast('Configuration inventaire enregistrée avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast("Erreur lors de l'enregistrement de la configuration inventaire", {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingInventorySettings = false
      }
    },
    async fetchPendingCleanupSettings() {
      this.loadingPendingCleanupSettings = true
      try {
        const { data } = await axios.get('/api/core/parameters/pending-order-cleanup/config/')
        this.pendingCleanupConfig = {
          auto_delete_pending_orders_enabled: Boolean(data.auto_delete_pending_orders_enabled),
          retention_days: data.retention_days || 10,
        }
      } catch (error) {
        this.$bvToast.toast('Erreur lors du chargement des paramètres de suppression automatique', {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingPendingCleanupSettings = false
      }
    },
    async savePendingCleanupSettings() {
      this.loadingPendingCleanupSettings = true
      try {
        const payload = {
          auto_delete_pending_orders_enabled: Boolean(this.pendingCleanupConfig.auto_delete_pending_orders_enabled),
        }
        const { data } = await axios.put('/api/core/parameters/pending-order-cleanup/config/', payload)
        this.pendingCleanupConfig = {
          auto_delete_pending_orders_enabled: Boolean(data.auto_delete_pending_orders_enabled),
          retention_days: data.retention_days || 10,
        }

        this.$bvToast.toast('Paramètres de suppression automatique enregistrés avec succès', {
          title: 'Succès',
          variant: 'success',
        })
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error || 'Erreur lors de l\'enregistrement des paramètres de suppression automatique',
          {
            title: 'Erreur',
            variant: 'danger',
          },
        )
      } finally {
        this.loadingPendingCleanupSettings = false
      }
    },

    // ── Authentication Configuration ──
    async fetchAuthConfig() {
      this.loadingAuth = true
      try {
        const { company } = storeAuth.state
        if (company) {
          this.authConfig = {
            auth_method: company.auth_method || 'email',
            phone_unique: company.phone_unique || false,
          }
          this.currentAuthConfig = { ...this.authConfig }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching auth config:', error)
      } finally {
        this.loadingAuth = false
      }
    },
    async saveAuthConfig() {
      this.loadingAuth = true
      try {
        const { company } = storeAuth.state
        if (!company || !company.id) {
          this.$bvToast.toast('Entreprise introuvable', {
            title: 'Erreur',
            variant: 'danger',
          })
          return
        }

        const payload = {
          auth_method: this.authConfig.auth_method,
          phone_unique: this.authConfig.auth_method === 'email' ? false : this.authConfig.phone_unique,
        }

        await axios.patch(`api/companies/update/${company.id}/`, payload)

        this.currentAuthConfig = { ...payload }

        // Refresh company in Vuex store
        await storeAuth.dispatch('fetchCompanyInfo')

        this.$bvToast.toast('Configuration d\'authentification mise à jour', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving auth config:', error)
        const msg = error.response?.data?.detail
          || error.response?.data?.error
          || 'Erreur lors de l\'enregistrement'
        this.$bvToast.toast(msg, {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingAuth = false
      }
    },
    // ── Invoice Configuration ──
    async fetchInvoiceConfig() {
      this.loadingInvoiceConfig = true
      try {
        const { company } = storeAuth.state
        if (company) {
          this.invoiceConfig = {
            allow_invoices_without_delivered_orders: company.allow_invoices_without_delivered_orders !== false,
          }
          this.currentInvoiceConfig = { ...this.invoiceConfig }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching invoice config:', error)
      } finally {
        this.loadingInvoiceConfig = false
      }
    },
    async saveInvoiceConfig() {
      this.loadingInvoiceConfig = true
      try {
        const { company } = storeAuth.state
        if (!company || !company.id) {
          this.$bvToast.toast('Entreprise introuvable', {
            title: 'Erreur',
            variant: 'danger',
          })
          return
        }

        const payload = {
          allow_invoices_without_delivered_orders: this.invoiceConfig.allow_invoices_without_delivered_orders,
        }

        await axios.patch(`api/companies/update/${company.id}/`, payload)

        this.currentInvoiceConfig = { ...payload }

        await storeAuth.dispatch('fetchCompanyInfo')

        this.$bvToast.toast('Configuration des factures fournisseur mise à jour', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving invoice config:', error)
        const msg = error.response?.data?.detail
          || error.response?.data?.error
          || 'Erreur lors de l\'enregistrement'
        this.$bvToast.toast(msg, {
          title: 'Erreur',
          variant: 'danger',
        })
      } finally {
        this.loadingInvoiceConfig = false
      }
    },

    authMethodLabel(method) {
      const labels = {
        email: 'Email uniquement',
        phone: 'Téléphone uniquement',
        both: 'Email et téléphone',
      }
      return labels[method] || method
    },
  },
}
</script>

<style scoped>
.parameters-container {
  padding: 1.5rem;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Header Styles */
.page-header {
  background: linear-gradient(90deg, #f7fafc 0%, #e3e8ee 100%);
  border-radius: 12px;
  padding: 2rem;
  color: #1a202c;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(227, 6, 19, 0.08);
  border: 1.5px solid #e3e8ee;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1a202c;
}

.title-icon {
  font-size: 1.75rem;
  opacity: 0.9;
  color: #ED1919;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
  color: #4a5568;
}

/* Card Styles */
.config-card,
.info-card {
  margin-bottom: 2rem;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.card-header-custom {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #ffffff;
  color: #1a202c;
  padding: 1rem 1.5rem;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #e5e7eb;
  border-left: 4px solid #ED1919;
}

.card-header-custom .icon {
  font-size: 1.5rem;
  color: #ED1919;
}

.card-header-custom h4 {
  margin: 0;
  font-weight: 600;
}

/* Form Styles */
.config-content,
.info-content {
  padding: 1.5rem;
}

.config-description {
  color: #4a5568;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.fee-line-card {
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #fff;
}

.fee-line-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.fee-operation-pill {
  display: inline-flex;
  align-items: center;
  margin-left: 0.75rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #842029;
  background: #f8d7da;
}

.form-group-enhanced {
  margin-bottom: 1.5rem;
}

.form-group-enhanced label {
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input-enhanced {
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  padding: 0.75rem 4rem 0.75rem 1rem;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.form-input-enhanced:focus {
  border-color: #ED1919;
  box-shadow: 0 0 0 3px rgba(227, 6, 19, 0.1);
}

.input-suffix {
  position: absolute;
  right: 1rem;
  font-weight: 600;
  color: #718096;
}

.fee-type-buttons {
  width: 100%;
}

.fee-type-buttons >>> .btn {
  flex: 1;
}

/* Supplier Selection */
.supplier-list {
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
}

.supplier-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.supplier-item:hover {
  background: #f7fafc;
}

.supplier-item.selected {
  background: #e6f7ff;
  border: 1px solid #91d5ff;
}

.supplier-info {
  display: flex;
  flex-direction: column;
}

.supplier-name-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.selected-count {
  margin-top: 0.5rem;
  font-weight: 600;
  color: #ED1919;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.save-btn {
  background: linear-gradient(90deg, #ED1919 0%, #ED1919 100%);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(227, 6, 19, 0.3);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Current Configuration Display */
.current-config-display {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #166534;
}

.current-config-display i {
  font-size: 1.2rem;
}

.config-summary {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Auth Method Info */
.auth-method-info {
  margin-bottom: 1.5rem;
}

.method-badge {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  font-size: 0.95rem;
}

.method-badge.email {
  background: #e8f4fd;
  border: 1px solid #90cdf4;
  color: #2b6cb0;
}

.method-badge.phone {
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  color: #276749;
}

.method-badge.both {
  background: #faf5ff;
  border: 1px solid #d6bcfa;
  color: #553c9a;
}

.phone-unique-toggle {
  padding: 0.5rem 0;
}

.cleanup-toggle-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border: 1px solid #dbe3ea;
  border-radius: 10px;
}

.cleanup-retention-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  background: #e8f4fd;
  color: #1f5f94;
  font-weight: 700;
  white-space: nowrap;
}

.display-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.supplier-display-section-title {
  color: #0A2634;
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.display-field-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 72px;
  padding: 0.9rem 1rem;
  background: #fff;
  border: 1px solid #dbe3ea;
  border-radius: 8px;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.display-field-toggle.active {
  background: #f8fbff;
  border-color: #82b5e8;
}

.display-field-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.display-field-copy strong,
.display-field-toggle--compact span {
  color: #1f2d3d;
  font-size: 0.98rem;
  line-height: 1.2;
}

.display-field-copy small {
  color: #6e7f91;
  font-size: 0.78rem;
  line-height: 1.3;
  margin-top: 0.25rem;
}

.display-field-switch {
  flex: 0 0 auto;
  margin: 0;
}

.supplier-display-overrides {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0.75rem 0 1rem;
}

.supplier-display-empty {
  margin-top: 0.75rem;
  padding: 0.85rem 1rem;
  color: #6e7f91;
  background: #f8fafc;
  border: 1px dashed #c8d4df;
  border-radius: 8px;
}

.supplier-display-override {
  padding: 1rem;
  background: #fff;
  border: 1px solid #dbe3ea;
  border-radius: 8px;
}

.supplier-display-override-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
}

.supplier-display-remove {
  width: 38px;
  min-width: 38px;
  height: 38px;
  padding: 0;
}

.display-fields-grid--compact {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.display-field-toggle--compact {
  min-height: 48px;
  padding: 0.6rem 0.75rem;
  background: #f8fafc;
}

@media (max-width: 576px) {
  .cleanup-toggle-panel {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
